<template>
    <div id="ImageProcessingSettings">
        <div class="image-processing-settings">
            <h2>{{ $t("chatbotv2.imageProcessingSettings.title") }}</h2>
            <p>{{ $t("chatbotv2.imageProcessingSettings.description") }}</p>
            
            <div class="processing-mode">
                <strong>{{ $t("chatbotv2.imageProcessingSettings.processingMode") }}</strong>
                <div class="mode-items">
                    <FeatureItem
                        :title="$t('chatbotv2.imageProcessingSettings.processWithBlock')"
                        icon="ChatAiIcon"
                        v-model="imageProcessingBlock"
                        :description="$t('chatbotv2.imageProcessingSettings.processWithBlockDesc')"
                        type="switch"
                        v-if="story.ai"
                    />

                    <FeatureItem
                        :title="$t('chatbotv2.imageProcessingSettings.processWithAI')"
                        icon="ChatAiIcon"
                        v-model="imageProcessingAI"
                        :description="$t('chatbotv2.imageProcessingSettings.processWithAIDesc')"
                        type="switch"
                        v-if="story.ai"
                    />
                </div>
            </div>

            <!-- Block İşleme Ayarları -->
            <div v-if="imageProcessingBlock && story.ai" class="block-processing-settings">
                <div class="block-selection">
                    <strong>{{ $t("chatbotv2.imageProcessingSettings.fallbackBlock") }}</strong>
                    <p>{{ $t("chatbotv2.imageProcessingSettings.fallbackBlockDesc") }}</p>
                    <BlockPicker 
                        :flatted-diagram="flattedDiagram" 
                        v-model="imageFallbackBlockId"
                        :placeholder="$t('chatbotv2.imageProcessingSettings.fallbackBlockPlaceholder')"
                    />
                </div>
                
                <div class="block-selection">
                    <strong>{{ $t("chatbotv2.imageProcessingSettings.directJumpBlock") }}</strong>
                    <p>{{ $t("chatbotv2.imageProcessingSettings.directJumpBlockDesc") }}</p>
                    <BlockPicker 
                        :flatted-diagram="flattedDiagram" 
                        v-model="imageDirectJumpBlockId"
                        :placeholder="$t('chatbotv2.imageProcessingSettings.directJumpBlockPlaceholder')"
                    />
                </div>
            </div>

            <!-- AI İşleme Ayarları -->
            <div v-if="imageProcessingAI && story.ai" class="ai-processing-settings">
                <div class="ai-mode">
                    <strong>{{ $t("chatbotv2.imageProcessingSettings.aiProcessingMode") }}</strong>
                    <div class="mode-items">
                        <FeatureItem
                            :title="$t('chatbotv2.imageProcessingSettings.processIndividual')"
                            icon="ChatAiIcon"
                            v-model="aiProcessingIndividual"
                            :description="$t('chatbotv2.imageProcessingSettings.processIndividualDesc')"
                            type="switch"
                            v-if="story.ai"
                        />

                        <FeatureItem
                            :title="$t('chatbotv2.imageProcessingSettings.processParallel')"
                            icon="ChatAiIcon"
                            v-model="aiProcessingParallel"
                            :description="$t('chatbotv2.imageProcessingSettings.processParallelDesc')"
                            type="switch"
                            v-if="story.ai"
                        />
                    </div>
                </div>

                <div v-if="aiProcessingParallel" class="buffer-time-setting">
                    <FeatureItem
                        :title="$t('chatbotv2.imageProcessingSettings.bufferTime')"
                        icon="ChatAiIcon"
                        v-model="imageBufferTime"
                        :description="$t('chatbotv2.imageProcessingSettings.bufferTimeDesc')"
                        type="number"
                        :min="5"
                        :max="120"
                        :step="5"
                        v-if="story.ai"
                        number-format="integer"
                        @blur="changeImageBufferTime(imageBufferTime)"
                        @increase="changeImageBufferTime(imageBufferTime)"
                        @decrease="changeImageBufferTime(imageBufferTime)"
                    />
                </div>
            </div>

            <div class="settings-btn">
                <SupsisButton ghost @click="$emit('close')">{{ $t("common.cancel") }}</SupsisButton>
                <SupsisButton primary @click="saveSettings">{{ $t("common.save") }}</SupsisButton>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import SupsisButton from "../../../Settings/components/SupsisButton.vue";
import FeatureItem from "../../../Settings/components/FeatureItem.vue";
import BlockPicker from "../../pages/dashboard/pickers/BlockPicker.vue";
import { sendSocketMessage } from "../../../../../utils/socketStream";
import log2 from "../../../../../utils/log2";
import _ from "lodash";
import StringUtils from "../../../../../utils/StringUtils";

const log = log2("ImageProcessingSettings");

export default {
    name: "ImageProcessingSettings",
    components: {
        FeatureItem,
        SupsisButton,
        BlockPicker,
    },
    props: {
        storyId: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            story: {},
            
            // Resim işleme ayarları
            imageProcessingBlock: false,
            imageProcessingAI: true,
            imageFallbackBlockId: null,
            imageDirectJumpBlockId: null,
            aiProcessingIndividual: true,
            aiProcessingParallel: false,
            imageBufferTime: 30,
            
            // Initialization flag
            initialized: false,
        };
    },
    computed: {
        ...mapState({
            stories: (state) => state.CB.stories,
            site: (state) => state.site.site,
            boards: (state) => state.CB.boards,
        }),
        flattedDiagram() {
            const board = this.boards.find(b => b.story === this.storyId);
            if (!board || !board.diagram) return [];
            
            const flattenDiagram = (node, path = "", parent = null) => {
                const result = [{
                    ...node,
                    path,
                    parent
                }];
                
                if (node.children && Array.isArray(node.children)) {
                    node.children.forEach((child, index) => {
                        const childPath = path ? `${path}.children[${index}]` : `children[${index}]`;
                        result.push(...flattenDiagram(child, childPath, node));
                    });
                }
                
                return result;
            };
            
            return flattenDiagram(board.diagram);
        },
    },
    mounted() {
        const story = this.stories.find((story) => story.id === this.storyId);
        
        // Resim işleme ayarlarının default değerleri
        if (story.ai && !story.ai.imageProcessing) {
            story.ai.imageProcessing = {
                mode: 'ai',
                blockSettings: {
                    fallbackBlockId: null,
                    directJumpBlockId: null
                },
                aiSettings: {
                    processingMode: 'individual',
                    bufferTime: 30
                }
            };
        }

        this.story = story;
        
        // Resim işleme ayarlarını yükle
        const imageProcessing = story.ai?.imageProcessing || {};
        
        this.imageProcessingBlock = imageProcessing.mode === 'block';
        this.imageProcessingAI = imageProcessing.mode !== 'block';
        this.imageFallbackBlockId = imageProcessing.blockSettings?.fallbackBlockId;
        this.imageDirectJumpBlockId = imageProcessing.blockSettings?.directJumpBlockId;
        this.aiProcessingIndividual = imageProcessing.aiSettings?.processingMode !== 'parallel';
        this.aiProcessingParallel = imageProcessing.aiSettings?.processingMode === 'parallel';
        this.imageBufferTime = imageProcessing.aiSettings?.bufferTime || 30;
        
        this.initialized = true;
    },
    methods: {
        ...mapActions({
            updateStorySingleKey: "CB/updateStorySingleKey",
        }),
        async updateImageProcessingSettings() {
            if (!this.initialized) return;
            if (!this.story || !this.story.ai) return;
            
            const imageProcessing = {
                mode: this.imageProcessingBlock ? 'block' : 'ai',
                blockSettings: {
                    fallbackBlockId: this.imageFallbackBlockId,
                    directJumpBlockId: this.imageDirectJumpBlockId
                },
                aiSettings: {
                    processingMode: this.aiProcessingParallel ? 'parallel' : 'individual',
                    bufferTime: this.imageBufferTime
                }
            };
            
            var currentSettings = this.story.ai.imageProcessing;
            if (_.isEqual(currentSettings, imageProcessing)) {
                return;
            }
            
            this.story.ai.imageProcessing = imageProcessing;
            
            return this.updateStorySingleKey({
                id: this.story._id,
                key: "ai.imageProcessing",
                value: imageProcessing,
            });
        },
        async changeImageBufferTime(bufferTime) {
            this.imageBufferTime = bufferTime;
            await this.updateImageProcessingSettings();
        },
        async switchImageProcessingMode(mode) {
            if (!this.initialized) return;
            
            if (mode === 'block') {
                this.imageProcessingBlock = true;
                this.imageProcessingAI = false;
            } else {
                this.imageProcessingBlock = false;
                this.imageProcessingAI = true;
            }
            await this.updateImageProcessingSettings();
        },
        async switchAIProcessingMode(mode) {
            if (!this.initialized) return;
            
            if (mode === 'parallel') {
                this.aiProcessingParallel = true;
                this.aiProcessingIndividual = false;
            } else {
                this.aiProcessingParallel = false;
                this.aiProcessingIndividual = true;
            }
            await this.updateImageProcessingSettings();
        },
        async saveSettings() {
            try {
                await this.updateImageProcessingSettings();
                this.$message.success(this.$t("chatbotv2.imageProcessingSettings.saveSuccess"));
                sendSocketMessage("chatbot/v2 image-processing-changed", {
                    siteId: this.site._id,
                    storyId: this.story._id,
                    imageProcessing: this.story.ai.imageProcessing,
                    isAssistantAPI: !!this.story.ai?.assistantId,
                });
                this.$emit('close');
            } catch (error) {
                this.$message.error(this.$t("chatbotv2.imageProcessingSettings.saveError"));
                console.error(error);
            }
        },
    },
    watch: {
        imageProcessingBlock: {
            handler(newVal, oldVal) {
                if (!this.initialized) return;
                if (newVal && !oldVal) {
                    this.switchImageProcessingMode('block');
                }
            },
        },
        imageProcessingAI: {
            handler(newVal, oldVal) {
                if (!this.initialized) return;
                if (newVal && !oldVal) {
                    this.switchImageProcessingMode('ai');
                }
            },
        },
        aiProcessingIndividual: {
            handler(newVal, oldVal) {
                if (!this.initialized) return;
                if (newVal && !oldVal) {
                    this.switchAIProcessingMode('individual');
                }
            },
        },
        aiProcessingParallel: {
            handler(newVal, oldVal) {
                if (!this.initialized) return;
                if (newVal && !oldVal) {
                    this.switchAIProcessingMode('parallel');
                }
            },
        },
        imageFallbackBlockId: {
            handler(newVal, oldVal) {
                if (!this.initialized) return;
                this.updateImageProcessingSettings();
            },
        },
        imageDirectJumpBlockId: {
            handler(newVal, oldVal) {
                if (!this.initialized) return;
                this.updateImageProcessingSettings();
            },
        },
    },
};
</script>

<style lang="scss" scoped>
#ImageProcessingSettings {
    padding: 30px;
    max-height: 80vh;
    overflow-y: auto;
    overflow-x: hidden;
    max-width: 100%;
    box-sizing: border-box;

    .settings-btn {
        display: flex;
        gap: 20px;
        justify-content: flex-end;
        margin-top: 30px;
        padding-top: 20px;
        border-top: 1px solid #e8eef3;
    }

    .image-processing-settings {
        display: flex;
        flex-direction: column;
        gap: 20px;
        max-width: 100%;
        box-sizing: border-box;

        h2 {
            margin-bottom: 10px;
            color: #22272d;
        }

        p {
            color: #647283;
            margin-bottom: 20px;
        }

        .processing-mode,
        .block-processing-settings,
        .ai-processing-settings {
            margin-bottom: 20px;

            strong {
                display: block;
                margin-bottom: 10px;
                color: #22272d;
            }

            .mode-items {
                display: flex;
                gap: 10px;
                margin-bottom: 15px;
                max-width: 100%;
                flex-wrap: wrap;
                box-sizing: border-box;
            }
        }

        .block-selection {
            margin-bottom: 15px;
            max-width: 100%;
            box-sizing: border-box;

            strong {
                display: block;
                margin-bottom: 5px;
                color: #22272d;
                font-size: 14px;
            }

            p {
                margin-bottom: 8px;
                font-size: 12px;
                color: #9ba6b3;
            }
        }

        .buffer-time-setting {
            margin-top: 15px;
            max-width: 100%;
            overflow: hidden;
            
            ::v-deep .feature-item {
                max-width: 100%;
                
                .feature-content {
                    max-width: 100%;
                    
                    input {
                        max-width: 100%;
                    }
                }
                
                .number-input-wrapper {
                    max-width: 100%;
                    
                    input {
                        max-width: 100%;
                    }
                }
            }
        }
    }
}
</style>
